import axios from "axios";
import { useEffect, useState } from "react";
import { X, ShieldCheck, Minus, Plus, Package, Check } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PRODUCTS = [
  { id: "moringa-250g", name: "Pure Moringa Powder", size: "250g", price: 349, note: "The original green ritual", color: "#2d5a27", image: "/products/moringa.png" },
  { id: "onion-250g", name: "Onion Powder", size: "250g", price: 249, note: "Sweet, slow-dried onions", color: "#b08d4f", image: "/products/onion.png" },
  { id: "garlic-250g", name: "Garlic Powder", size: "250g", price: 249, note: "Bold and deeply aromatic", color: "#a89b6c", image: "/products/garlic.png" },
  { id: "tomato-250g", name: "Sun-Dried Tomatoes", size: "250g", price: 349, note: "Sun-ripened tang", color: "#b5552d", image: "/products/tomato.png" },
  { id: "mushroom-250g", name: "Dehydrated Mushroom", size: "250g", price: 649, note: "Shiitake variety — deep umami richness", color: "#7a6a53", image: "/products/mushroom.png" },
  { id: "pineapple-250g", name: "Dehydrated Pineapple", size: "250g", price: 349, note: "Golden tropical sweetness", color: "#d9a441", image: "/products/pineapple.png" },
  { id: "kiwi-250g", name: "Dehydrated Kiwi", size: "250g", price: 349, note: "Bright, zesty and tangy", color: "#7d9b3a", image: "/products/kiwi.png" },
  { id: "mango-250g", name: "Dehydrated Mango", size: "250g", price: 349, note: "Sun-soaked orchard flavour", color: "#d97b2d", image: "/products/mango.png" },
];

const loadRazorpayScript = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const script = document.createElement("script");
  script.src = "https://checkout.razorpay.com/v1/checkout.js";
  script.onload = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

const readLocalOrders = () => {
  try { return JSON.parse(localStorage.getItem("jamvanya_orders") || "[]"); }
  catch { return []; }
};

const saveLocalOrder = (order) => {
  try { localStorage.setItem("jamvanya_orders", JSON.stringify([order, ...readLocalOrders()].slice(0, 10))); }
  catch {}
};

export const CartModal = ({ cart, setCart, user, onClose }) => {
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", phone: "", address: "", pincode: "" });
  const [paying, setPaying] = useState(false);
  const items = PRODUCTS.filter(p => (cart[p.id] || 0) > 0).map(p => ({ ...p, quantity: cart[p.id] }));
  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const discount = subtotal > 999 ? Math.round(subtotal * 0.2) : 0;
  const total = subtotal - discount;
  const setQty = (id, q) => setCart({ ...cart, [id]: Math.min(20, Math.max(0, q)) });
  const summary = items.map(i => `${i.quantity} x ${i.name} ${i.size}`).join(", ");

  const [confirmed, setConfirmed] = useState(null);

  const finishOrder = (status) => {
    const record = { id: `JV-${Date.now().toString().slice(-8)}`, date: new Date().toISOString(), items: summary, total, status };
    saveLocalOrder(record);
    setCart({});
    try { localStorage.setItem("jamvanya_cart", "{}"); } catch {}
    setConfirmed({ ...record, name: form.name, phone: form.phone, address: form.address, pincode: form.pincode });
  };

  const fallbackToEnquiry = async () => {
    await axios.post(`${API}/enquiries`, { name: form.name, email: form.email, phone: form.phone, quantity: `${summary} (Rs.${total})`, message: `Delivery address: ${form.address}, PIN ${form.pincode}`, enquiry_type: "order" });
    finishOrder("Placed · confirmation call pending");
  };

  const pay = async (e) => {
    e.preventDefault();
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/orders/create`, { ...form, items: items.map(i => ({ product: i.id, quantity: i.quantity })) });
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("script");
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: "INR",
        name: "Jamvanya Private Limited",
        description: summary,
        order_id: data.order_id,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#2d5a27" },
        handler: async (res) => {
          try {
            await axios.post(`${API}/orders/verify`, res);
            finishOrder("Paid");
          } catch {
            toast.error("Payment verification failed", { description: "Please call us at +91 7208656816." });
          }
        },
      });
      rzp.on("payment.failed", () => toast.error("Payment failed", { description: "Please try again." }));
      rzp.open();
    } catch (err) {
      if (err?.response?.status === 503) {
        try { await fallbackToEnquiry(); }
        catch { toast.error("Something went wrong", { description: "Please try again in a moment." }); }
      } else if (err?.response?.status === 400 && err?.response?.data?.detail) {
        toast.error("Could not place order", { description: err.response.data.detail });
      } else {
        toast.error("Something went wrong", { description: "Please try again in a moment." });
      }
    } finally {
      setPaying(false);
    }
  };

  if (confirmed) {
    return (
      <div className="modal-backdrop" data-testid="order-confirmation">
        <div className="modal-card confirm-card">
          <div className="confirm-icon"><Check size={30}/></div>
          <h2>{confirmed.status === "Paid" ? "Order confirmed!" : "Order placed!"}</h2>
          <p className="modal-intro">{confirmed.status === "Paid" ? "Payment received — a confirmation email is on its way to you." : "Our team will call you shortly to confirm and arrange payment & delivery."}</p>
          <div className="confirm-rows" data-testid="confirm-summary">
            <div><span>Order</span><b>{confirmed.id}</b></div>
            <div><span>Items</span><b>{confirmed.items}</b></div>
            <div><span>{confirmed.status === "Paid" ? "Amount paid" : "Total"}</span><b>₹{confirmed.total.toLocaleString("en-IN")}</b></div>
            <div><span>Deliver to</span><b>{confirmed.name} · {confirmed.phone}</b></div>
            <div><span>Address</span><b>{confirmed.address}, PIN {confirmed.pincode}</b></div>
          </div>
          <button className="button button-primary full" onClick={onClose} data-testid="confirm-continue-button">Continue shopping</button>
          <span className="pay-note">Questions? Call or WhatsApp +91 7208656816 anytime</span>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" data-testid="cart-modal">
      <div className="modal-card">
        <button className="close-btn" onClick={onClose} data-testid="close-cart-button"><X size={20}/></button>
        <span className="eyebrow">Fresh from the farm</span>
        <h2>Your cart.</h2>
        {items.length === 0 ? (
          <div className="cart-empty" data-testid="cart-empty-state">
            <p className="modal-intro">Your cart is empty. Add a pouch to get started — sun-dried staples from Jamkhed, Maharashtra.</p>
            <div className="pack-row">
              {PRODUCTS.filter(p => p.id.startsWith("moringa")).map(p => (
                <button type="button" key={p.id} className="pack-option" onClick={() => setQty(p.id, 1)} data-testid={`quick-add-${p.id}`}>{p.name} {p.size}<small>₹{p.price} per pouch</small></button>
              ))}
            </div>
          </div>
        ) : (
          <form onSubmit={pay} data-testid="cart-checkout-form">
            <div className="cart-items">
              {items.map(i => (
                <div className="qty-row" key={i.id} data-testid={`cart-item-${i.id}`}>
                  <span className="qty-label">{i.name}<small>{i.size} pouch · ₹{i.price} each</small></span>
                  <span className="qty-stepper">
                    <button type="button" onClick={() => setQty(i.id, i.quantity - 1)} data-testid={`cart-decrease-${i.id}`}><Minus size={14}/></button>
                    <b data-testid={`cart-qty-${i.id}`}>{i.quantity}</b>
                    <button type="button" onClick={() => setQty(i.id, i.quantity + 1)} data-testid={`cart-increase-${i.id}`}><Plus size={14}/></button>
                  </span>
                </div>
              ))}
            </div>
            <input required placeholder="Your name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="order-name-input"/>
            <input required type="email" placeholder="Email address" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="order-email-input"/>
            <input required placeholder="Phone number (for delivery)" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="order-phone-input"/>
            <textarea required placeholder="Delivery address (house, street, area, city)" value={form.address} onChange={e => setForm({...form, address: e.target.value})} data-testid="order-address-input"/>
            <input required placeholder="PIN code" inputMode="numeric" maxLength={6} pattern="[1-9][0-9]{5}" title="6-digit PIN code" value={form.pincode} onChange={e => setForm({...form, pincode: e.target.value.replace(/\D/g, "")})} data-testid="order-pincode-input"/>
            {discount > 0 && <div className="price-row" data-testid="discount-row"><span className="discount-label">Offer applied: 20% off above ₹999</span><b className="discount-amt" data-testid="order-discount-amount">− ₹{discount.toLocaleString("en-IN")}</b></div>}
            <div className="price-row">
              <span>{total >= 999 ? "Free delivery included" : "Delivery calculated on confirmation"}</span>
              <span className="total-stack">
                {discount > 0 && <s data-testid="order-subtotal-price">₹{subtotal.toLocaleString("en-IN")}</s>}
                <b data-testid="order-total-price">₹{total.toLocaleString("en-IN")}</b>
              </span>
            </div>
            <button className="button button-primary full" disabled={paying} data-testid="pay-now-button">
              {paying ? "Preparing checkout…" : `Pay ₹${total.toLocaleString("en-IN")} securely`}<ShieldCheck size={16}/>
            </button>
          </form>
        )}
        <span className="pay-note" data-testid="pay-note"><ShieldCheck size={13}/> Secure checkout via Razorpay · Cards, UPI, netbanking</span>
      </div>
    </div>
  );
};

export const OrdersModal = ({ user, onClose }) => {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      let remote = [];
      try {
        const { data } = await axios.get(`${API}/orders/my`);
        remote = data.map(o => ({
          id: `JV-${o.id.replace(/-/g, "").slice(-8).toUpperCase()}`,
          date: o.created_at,
          items: o.items.map(i => `${i.quantity} x ${i.name} ${i.size}`).join(", "),
          total: o.amount / 100,
          status: o.status === "paid" ? "Paid" : "Payment pending",
        }));
      } catch {}
      if (cancelled) return;
      setOrders([...remote, ...readLocalOrders()].sort((a, b) => new Date(b.date) - new Date(a.date)));
    };
    load();
    return () => { cancelled = true; };
  }, []);
  return (
    <div className="modal-backdrop" data-testid="orders-modal">
      <div className="modal-card">
        <button className="close-btn" onClick={onClose} data-testid="close-orders-button"><X size={20}/></button>
        <span className="eyebrow">{user?.name ? `${user.name.split(" ")[0]}'s account` : "Your account"}</span>
        <h2>Recent orders.</h2>
        {orders === null ? (
          <p className="modal-intro">Loading your orders…</p>
        ) : orders.length === 0 ? (
          <p className="modal-intro" data-testid="orders-empty-state">No orders yet — your recent orders will appear here after you checkout.</p>
        ) : (
          <div className="order-history" data-testid="order-history-list">
            {orders.map(o => (
              <div className="order-history-item" key={o.id} data-testid="order-history-item">
                <div className="order-history-top">
                  <span className="order-id"><Package size={13}/> {o.id}</span>
                  <span className={`order-status ${o.status === "Paid" ? "paid" : ""}`} data-testid="order-status-chip">{o.status}</span>
                </div>
                <span className="order-items">{o.items}</span>
                <div className="order-history-bottom">
                  <span>{new Date(o.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                  <b>₹{o.total.toLocaleString("en-IN")}</b>
                </div>
              </div>
            ))}
          </div>
        )}
        <span className="pay-note">Questions about an order? Call +91 7208656816</span>
      </div>
    </div>
  );
};
